import React from 'react';
import { FormattedMessage } from '../../../util/reactIntl';

import css from './NoAvailableResultsMaybe.module.css';

/**
 * Shown when every supplier on the page turned out to be booked for the searched date.
 *
 * A sibling of NoSearchResultsMaybe rather than an extension of it, because these are two
 * different situations and only one of them is a failed search. NoSearchResultsMaybe fires on
 * `totalItems === 0` and says "Es konnten keine Einträge gefunden werden, die deinen
 * Suchkriterien entsprechen" over a "Filter zurücksetzen" button. Here the search DID find
 * suppliers — the count in the header is real — they are simply all busy on the chosen date, and
 * clearing the filters would throw away the date and the search along with them. Widening that
 * component's condition would have made its own message untrue whenever this case fired.
 *
 * There is no control that puts the booked suppliers back, so the message offers no button. It
 * names the one move the visitor still has, and which move that is depends on the result set:
 *
 * - Other pages exist: availability is only ever resolved for the current page's ids, so
 *   another page may well hold suppliers who are free. "Andere", not "weitere" — on the last
 *   page the other pages are behind the visitor, not ahead.
 * - This page is the whole result set: there is nowhere else to look, so the only thing left to
 *   change is the period.
 *
 * BOTH strings are scoped to "auf dieser Seite", because one page is all that was ever probed.
 * An unqualified "alle Anbieter sind belegt" would be a claim about the whole catalogue made on
 * the strength of a single page's answer — and on the last page of a multi-page set the pager
 * sits directly beneath it, offering the pages the sentence just wrote off.
 *
 * @component
 * @param {Object} props
 * @param {boolean} [props.hasOtherPages] whether the result set has any page but this one
 * @returns {JSX.Element}
 */
const NoAvailableResultsMaybe = props => {
  const { hasOtherPages = false } = props;

  return (
    <div className={css.noAvailableResults}>
      <FormattedMessage
        id={hasOtherPages ? 'SearchPage.allListingsBooked' : 'SearchPage.allListingsBookedOnePage'}
      />
    </div>
  );
};

export default NoAvailableResultsMaybe;
